import React, { useState } from 'react';
import { Container, Paper, Typography, TextField, Button, Box, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { signOut } from '../utils/api';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../utils/passwordValidation';

/**
 * ChangePassword Component
 * Forces users to change their password on first login
 * Shows when requiresPasswordChange flag is true
 */
const ChangePassword = () => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate new password
        const { isValid, errors } = validatePassword(newPassword);
        if (!isValid) {
            setSnackbar({
                open: true,
                message: `Password does not meet requirements: ${errors.join(', ')}`,
                severity: 'error'
            });
            return;
        }

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setSnackbar({
                open: true,
                message: 'New passwords do not match',
                severity: 'error'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.put('/auth/update-password', {
                oldPassword,
                newPassword
            });

            setSnackbar({
                open: true,
                message: response.data.message || 'Password updated successfully. Please log in again.',
                severity: 'success'
            });

            // Wait 2 seconds then redirect to login
            setTimeout(() => {
                signOut();
            }, 2000);

        } catch (error) {
            console.error('Password change failed:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to update password. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkipForNow = () => {
        // Allow user to skip but show warning
        setSnackbar({
            open: true,
            message: 'You can change your password later from your profile page',
            severity: 'info'
        });
        setTimeout(() => {
            navigate('/dashboard');
        }, 1500);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: '#f5f7fa',
                padding: { xs: '20px', sm: '0' },
            }}
        >
            <Box
                component="img"
                src="/Brand_logo.svg"
                alt="Brand Logo"
                sx={{
                    width: { xs: '80%', sm: '400px' },
                    height: 'auto',
                    mb: 2,
                }}
            />

            <Container
                maxWidth="sm"
                sx={{
                    mt: -4,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <Lock sx={{ fontSize: 40, color: 'warning.main', mr: 1 }} />
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: 'warning.main',
                            }}
                        >
                            Change Password
                        </Typography>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Password Change Required
                        </Typography>
                        <Typography variant="body2">
                            For security reasons, you must change your password before continuing.
                            Please create a strong password that meets all the requirements below.
                        </Typography>
                    </Alert>

                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="Current Password"
                            variant="outlined"
                            type={showOldPassword ? 'text' : 'password'}
                            fullWidth
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            margin="normal"
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowOldPassword(!showOldPassword)}
                                            edge="end"
                                        >
                                            {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />

                        <TextField
                            label="New Password"
                            variant="outlined"
                            type={showNewPassword ? 'text' : 'password'}
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            margin="normal"
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />

                        {/* Password Requirements Display */}
                        <PasswordRequirements password={newPassword} />

                        <TextField
                            label="Confirm New Password"
                            variant="outlined"
                            type={showConfirmPassword ? 'text' : 'password'}
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            margin="normal"
                            required
                            error={confirmPassword && newPassword !== confirmPassword}
                            helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            type="submit"
                            disabled={isSubmitting}
                            sx={{
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                mb: 1,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                }
                            }}
                        >
                            {isSubmitting ? 'Updating Password...' : 'Change Password'}
                        </Button>

                        <Button
                            variant="text"
                            color="secondary"
                            fullWidth
                            onClick={handleSkipForNow}
                            disabled={isSubmitting}
                            sx={{
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                textTransform: 'none',
                            }}
                        >
                            Skip for now (not recommended)
                        </Button>
                    </form>
                </Paper>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ChangePassword;
